import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { User, Building2, MapPin, Loader2, ArrowRight, Info, FileText, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import ResponsibilityNotice from '../components/ResponsibilityNotice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { key: 'parlamentar', label: 'Por parlamentar', icon: User,       placeholder: 'Digite o nome do deputado ou senador' },
  { key: 'municipio',   label: 'Por município',   icon: MapPin,     placeholder: 'Digite o nome da cidade' },
  { key: 'instituicao', label: 'Por instituição', icon: Building2,  placeholder: 'Hospital, escola, ONG, CNPJ...' },
  { key: 'area',        label: 'Por área',        icon: FileText,   placeholder: '' },
];

const Emendas = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('parlamentar');
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [areas, setAreas] = useState([]);
  const [error, setError] = useState('');
  const [status, setStatus] = useState(null);

  const [areaAtiva, setAreaAtiva] = useState(null);
  const [areaRanking, setAreaRanking] = useState(null);
  const [loadingArea, setLoadingArea] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/emendas/areas`, { timeout: 8000 })
      .then((r) => setAreas(r.data.areas || []))
      .catch(() => setAreas([]));
    axios.get(`${API}/public/emendas/status`, { timeout: 8000 })
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  }, []);

  const search = async () => {
    if (tab === 'area') return;
    if (!q.trim() || q.trim().length < 3) {
      setError('Informe pelo menos 3 caracteres.');
      return;
    }
    setLoading(true);
    setError('');
    setResults(null);
    try {
      const params = {};
      params[tab] = q.trim();
      const resp = await axios.get(`${API}/public/emendas/buscar`, { params, timeout: 15000 });
      setResults(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Falha na busca. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const goByArea = async (areaKey) => {
    setAreaAtiva(areaKey);
    setLoadingArea(true);
    setAreaRanking(null);
    try {
      const r = await axios.get(`${API}/public/emendas/por-area/${areaKey}`, { timeout: 15000 });
      setAreaRanking(r.data);
    } catch (e) {
      setAreaRanking({ ok: false, error: e.response?.data?.detail || 'Não foi possível carregar o ranking desta área.' });
    } finally {
      setLoadingArea(false);
    }
  };

  const currentTab = TABS.find((t) => t.key === tab);

  return (
    <div className="max-w-4xl mx-auto space-y-5" data-testid="emendas-page">
      {/* Header */}
      <div className="text-center py-2">
        <div className="w-14 h-14 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto mb-3">
          <FileText className="w-7 h-7 text-[#006030]" />
        </div>
        <h1 className="text-2xl md:text-3xl font-black text-[#0F1C2E] tracking-tight">
          Emendas Parlamentares
        </h1>
        <p className="text-sm text-[#475569] mt-1 max-w-xl mx-auto">
          Para onde foi o dinheiro do seu deputado ou senador. Dados oficiais em linguagem simples.
        </p>
      </div>

      {/* Aviso educativo */}
      <Card className="p-4 border border-[#DBEAFE] bg-[#EFF6FF]" data-testid="emendas-educational-banner">
        <div className="flex items-start gap-2 text-sm text-[#1E40AF]">
          <Info className="w-5 h-5 shrink-0 mt-0.5" />
          <p className="leading-relaxed">
            As emendas parlamentares são recursos públicos. Cada deputado e senador tem direito de destinar valores para obras, serviços e instituições. Os dados vêm do Portal da Transparência do Governo Federal.
          </p>
        </div>
      </Card>

      {/* Última atualização */}
      {status?.has_real_data && status.last_updated && (
        <p className="text-[11px] text-[#94A3B8] text-center" data-testid="emendas-last-updated">
          Base oficial atualizada em {new Date(status.last_updated).toLocaleDateString('pt-BR')}.{' '}
          {status.num_rows?.toLocaleString('pt-BR')} emendas indexadas desde {status.min_ano}.
        </p>
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1" role="tablist">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => { setTab(t.key); setResults(null); setQ(''); setError(''); }}
              className={cn(
                'flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-semibold transition-colors whitespace-nowrap',
                tab === t.key
                  ? 'bg-[#006030] text-white'
                  : 'bg-white text-[#475569] hover:bg-[#F4F6F8] border border-[#E2E8F0]',
              )}
              data-testid={`emendas-tab-${t.key}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Search area */}
      {tab !== 'area' && (
        <Card className="p-4 md:p-5 border border-[#E2E8F0]" data-testid="emendas-search-card">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              placeholder={currentTab.placeholder}
              className="flex-1"
              data-testid="emendas-search-input"
              minLength={3}
            />
            <Button
              onClick={search}
              disabled={loading || q.trim().length < 3}
              className="bg-[#006030] hover:bg-[#004020] text-white"
              data-testid="emendas-search-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
            </Button>
          </div>
          {error && (
            <div className="mt-3 flex items-start gap-2 text-xs text-[#C62828]" data-testid="emendas-search-error">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {error}
            </div>
          )}
        </Card>
      )}

      {/* Grid de áreas */}
      {tab === 'area' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="emendas-area-grid">
          {areas.map((a) => (
            <button
              key={a.key}
              type="button"
              onClick={() => goByArea(a.key)}
              className={cn(
                'p-4 border rounded-xl bg-white hover:border-[#006030] hover:shadow-md transition-all text-left',
                areaAtiva === a.key ? 'border-[#006030] shadow-md' : 'border-[#E2E8F0]',
              )}
              data-testid={`emendas-area-${a.key}`}
            >
              <div className="text-2xl mb-1">{a.emoji}</div>
              <p className="text-sm font-bold text-[#0F1C2E]">{a.label}</p>
            </button>
          ))}
        </div>
      )}

      {/* Ranking inline por área */}
      {tab === 'area' && areaAtiva && (
        <div className="space-y-3" data-testid="emendas-area-ranking">
          {loadingArea && (
            <div className="text-center py-6 text-sm text-[#475569]" data-testid="emendas-area-loading">
              <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Carregando ranking de {areas.find(a => a.key === areaAtiva)?.label}...
            </div>
          )}
          {!loadingArea && areaRanking?.ok && (
            <>
              {/* Resumo */}
              <Card className="p-4 border border-[#E2E8F0] bg-[#F0FDF4]" data-testid="emendas-area-resumo">
                <p className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold">
                  Total investido em {areas.find(a => a.key === areaAtiva)?.label}
                </p>
                <p className="text-2xl font-black text-[#006030] mt-1">
                  {(areaRanking.resumo?.total || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}
                </p>
                <p className="text-xs text-[#475569] mt-1">
                  {(areaRanking.resumo?.num_emendas || 0).toLocaleString('pt-BR')} pagamentos classificados por palavras-chave em favorecido/categoria/tipo.
                </p>
              </Card>

              {/* Top municípios */}
              {areaRanking.top_municipios?.length > 0 && (
                <Card className="p-4 border border-[#E2E8F0]" data-testid="emendas-area-top-muni">
                  <h3 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">
                    Municípios que mais receberam
                  </h3>
                  <div className="space-y-1.5">
                    {areaRanking.top_municipios.map((m, idx) => (
                      <div
                        key={m.cod_ibge || idx}
                        onClick={() => m.cod_ibge && navigate(`/emendas/municipio/${m.cod_ibge}`)}
                        className={cn(
                          'flex items-center justify-between gap-3 py-2 border-b border-[#F1F5F9] last:border-0 -mx-2 px-2 rounded',
                          m.cod_ibge && 'cursor-pointer hover:bg-[#F9FAFB]',
                        )}
                        data-testid={`emendas-area-muni-${idx}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#0F1C2E] truncate">
                            <span className="text-[#94A3B8] font-mono text-xs mr-1.5">{String(idx + 1).padStart(2, '0')}</span>
                            {m.nome} <span className="text-[#94A3B8]">/ {m.uf}</span>
                          </p>
                          <p className="text-[11px] text-[#94A3B8]">{m.num_emendas} pagamento(s)</p>
                        </div>
                        <span className="text-sm font-bold text-[#006030] shrink-0">
                          {(m.total || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}
                        </span>
                        {m.cod_ibge && <ArrowRight className="w-4 h-4 text-[#94A3B8] shrink-0" />}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Top instituições */}
              {areaRanking.top_instituicoes?.length > 0 && (
                <Card className="p-4 border border-[#E2E8F0]" data-testid="emendas-area-top-inst">
                  <h3 className="text-sm font-black text-[#0F1C2E] uppercase tracking-wider mb-3">
                    Instituições que mais receberam
                  </h3>
                  <div className="space-y-1.5">
                    {areaRanking.top_instituicoes.map((i, idx) => (
                      <div
                        key={i.cnpj || idx}
                        onClick={() => i.cnpj && navigate(`/empresa/${i.cnpj}`)}
                        className={cn(
                          'flex items-center justify-between gap-3 py-2 border-b border-[#F1F5F9] last:border-0 -mx-2 px-2 rounded',
                          i.cnpj && 'cursor-pointer hover:bg-[#F9FAFB]',
                        )}
                        data-testid={`emendas-area-inst-${idx}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#0F1C2E] truncate">
                            <span className="text-[#94A3B8] font-mono text-xs mr-1.5">{String(idx + 1).padStart(2, '0')}</span>
                            {i.nome || 'Sem nome'}
                          </p>
                          <p className="text-[11px] text-[#94A3B8]">{i.tipo} · {i.num_emendas} pagamento(s)</p>
                        </div>
                        <span className="text-sm font-bold text-[#006030] shrink-0">
                          {(i.total || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })}
                        </span>
                        {i.cnpj && <ArrowRight className="w-4 h-4 text-[#94A3B8] shrink-0" />}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {!areaRanking.top_municipios?.length && !areaRanking.top_instituicoes?.length && (
                <Card className="p-4 border border-[#FDE68A] bg-[#FEF3C7]" data-testid="emendas-area-empty">
                  <p className="text-sm text-[#92400E]">
                    Sem pagamentos identificados para essa área na base atual.
                  </p>
                </Card>
              )}

              <p className="text-[11px] text-[#94A3B8] text-center">
                Fonte: {areaRanking.fonte || 'Tesouro Transparente'}
              </p>
            </>
          )}
          {!loadingArea && areaRanking && !areaRanking.ok && (
            <Card className="p-4 border border-[#FCA5A5] bg-[#FEE2E2]" data-testid="emendas-area-error">
              <div className="flex items-start gap-2 text-[#991B1B]">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <p className="text-sm">{areaRanking.error || 'Não foi possível carregar o ranking.'}</p>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Results — parlamentares */}
      {results?.parlamentares?.length > 0 && (
        <div className="space-y-2" data-testid="emendas-results-parlamentares">
          {results.parlamentares.map((p) => (
            <Card
              key={p.id}
              onClick={() => navigate(`/emendas/parlamentar/${p.id}`)}
              className="p-3 border border-[#E2E8F0] cursor-pointer hover:border-[#006030] hover:shadow-sm transition-all"
              data-testid={`emendas-result-parl-${p.id}`}
            >
              <div className="flex items-center gap-3">
                {p.foto ? (
                  <img src={p.foto} alt={p.nome} className="w-12 h-12 rounded-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                    <User className="w-5 h-5 text-[#006030]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#0F1C2E] truncate">{p.nome}</p>
                  <p className="text-xs text-[#475569]">
                    {p.cargo || 'Parlamentar'} · {p.partido || '—'} · {p.uf || '—'}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Results — municipios */}
      {results?.municipios?.length > 0 && (
        <div className="space-y-2" data-testid="emendas-results-municipios">
          {results.municipios.map((m) => (
            <Card
              key={m.cod_ibge}
              onClick={() => navigate(`/emendas/municipio/${m.cod_ibge}`)}
              className="p-3 border border-[#E2E8F0] cursor-pointer hover:border-[#006030] transition-all"
              data-testid={`emendas-result-muni-${m.cod_ibge}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#006030]" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-sm text-[#0F1C2E]">{m.nome}</p>
                  <p className="text-xs text-[#475569]">{m.uf}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Results — instituicoes */}
      {results?.instituicoes?.length > 0 && (
        <div className="space-y-2" data-testid="emendas-results-instituicoes">
          {results.instituicoes.filter((i) => i.cnpj && String(i.cnpj).replace(/\D/g, '').length === 14).map((i) => (
            <Card
              key={i.cnpj}
              onClick={() => navigate(`/empresa/${i.cnpj}`)}
              className="p-3 border border-[#E2E8F0] cursor-pointer hover:border-[#006030] transition-all"
              data-testid={`emendas-result-inst-${i.cnpj}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#E8F5E9] flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-[#006030]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-[#0F1C2E] truncate">{i.nome}</p>
                  <p className="text-xs text-[#475569]">{i.tipo}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {results && !results.parlamentares?.length && !results.municipios?.length && !results.instituicoes?.length && (
        <div className="text-center py-8 text-sm text-[#94A3B8]" data-testid="emendas-empty">
          Nenhum resultado encontrado. Tente outro termo.
        </div>
      )}

      <ResponsibilityNotice
        source="Tesouro Transparente e Câmara dos Deputados"
        sourceUrl="https://www.tesourotransparente.gov.br/ckan/dataset/emendas-parlamentares-individuais-e-de-bancada"
        lastUpdated={status?.last_updated}
      />
    </div>
  );
};

export default Emendas;
